import type { EventHandler, MouseEvent } from 'react';

import type { LinkProps } from '@tanstack/react-router';
import { Link as RouterLink, useRouter } from '@tanstack/react-router';
import type { VariantProps } from 'class-variance-authority';
import { cva } from 'class-variance-authority';

import { LinkClicked } from 'src/ampli';
import { tracker } from 'src/analytics/tracker';
import { cn } from 'src/utils/classNamesHelper';

const linkVariants = cva('', {
  variants: {
    as: {
      button: 'ark-button font-medium text-18',
      link: 'ark-link',
      unstyled: '',
    },
    inline: {
      true: 'ark-link--inline',
    },
    variant: {
      primary: 'ark-button--primary',
      'primary-neutral': 'ark-button--primary-neutral',
      secondary: 'ark-button--secondary',
      tertiary: 'ark-button--tertiary',
      'tertiary-neutral': 'ark-button--tertiary-neutral',
    },
    loading: {
      true: 'ark-button--loading',
    },
    size: {
      small: 'ark-button-small',
      medium: 'ark-button-medium',
      large: 'ark-button-large',
    },
  },

  defaultVariants: {
    as: 'link',
  },
});

export type Link2Props = {
  /** Enables LinkClicked tracking events when set */
  componentLocation: string | undefined;
  className?: string;
  onClick?: EventHandler<MouseEvent<HTMLAnchorElement>>;
} & VariantProps<typeof linkVariants> &
  LinkProps;

// TODO: Rename to Link, when the old Link is removed
export function Link2({
  componentLocation,
  onClick,
  variant,
  inline,
  loading,
  size,
  as,
  className,
  ...props
}: Link2Props) {
  const router = useRouter();
  return (
    <RouterLink
      className={cn(
        linkVariants({ variant, inline, loading, size, as }),
        className
      )}
      onClick={(event) => {
        if (componentLocation) {
          const fullUrl = router.buildLocation({
            to: props.to,
            search: props.search,
          }).href;
          tracker.trackEvent(
            new LinkClicked({
              component_location: componentLocation,
              text: event.currentTarget.innerText,
              link_source: fullUrl ?? '',
            })
          );
        }
        onClick?.(event);
      }}
      {...props}
    />
  );
}
